import { api } from "@/lib/api";
import type { ParsedExtraction } from "@/features/upload/types";
import type { PaaExtraction } from "@/features/paa/types";
import type { DocumentDetail, DocumentSummary, DocumentType } from "./types";
import { typeSlug } from "./types";

// GET /api/documents?q=&take= — newest-first across both document types.
export async function listDocuments(
  q?: string,
  take = 50,
  signal?: AbortSignal,
): Promise<DocumentSummary[]> {
  const params = new URLSearchParams();
  if (q?.trim()) params.set("q", q.trim());
  params.set("take", String(take));
  return api<DocumentSummary[]>(`/api/documents?${params}`, { signal });
}

// GET /api/documents/{type}/{id} — summary + the saved extraction payload.
export async function getDocument(
  type: DocumentType,
  id: string,
  signal?: AbortSignal,
): Promise<DocumentDetail> {
  return api<DocumentDetail>(`/api/documents/${typeSlug(type)}/${id}`, {
    signal,
  });
}

// POST /api/documents/{type} — create a draft right after extraction.
// Returns the document id the flow threads through updates and generate.
export async function createDocument(
  type: DocumentType,
  payload: ParsedExtraction | PaaExtraction,
  sessionId?: string | null,
): Promise<string> {
  const qs = sessionId ? `?sessionId=${encodeURIComponent(sessionId)}` : "";
  const res = await api<{ id: string }>(
    `/api/documents/${typeSlug(type)}${qs}`,
    { method: "POST", body: JSON.stringify(payload) },
  );
  return res.id;
}

// PUT /api/documents/{type}/{id} — refresh the draft after manual edits
// (callers debounce; the server keeps any already-attached PDF).
export async function updateDocument(
  type: DocumentType,
  id: string,
  payload: ParsedExtraction | PaaExtraction,
): Promise<void> {
  await api<void>(`/api/documents/${typeSlug(type)}/${id}`, {
    method: "PUT",
    body: JSON.stringify(payload),
  });
}
